// Quiz-Logik: Mischen und Wertung aller Fragetypen (rein, testbar, auch serverseitig nutzbar).
import type { ClozeQuestion, EstimateQuestion, MatchQuestion, McQuestion, NumlockQuestion, OrderQuestion, Question } from './types.ts';

/** liefert eine zufällige Reihenfolge der Indizes 0..n-1 */
export function shuffleMc(n: number, rnd: () => number = Math.random): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Mischung, die garantiert nicht der Ausgangsreihenfolge entspricht (für Reihenfolge-Fragen) */
export function shuffleNotIdentity(n: number, rnd: () => number = Math.random): number[] {
  if (n < 2) return [0];
  for (;;) {
    const a = shuffleMc(n, rnd);
    if (a.some((v, i) => v !== i)) return a;
  }
}

export function checkMc(q: McQuestion, optionIndex: number): boolean {
  return q.answer === optionIndex;
}

/** Schätzfrage: relative Abweichung (0 = exakt) */
export function estimateError(q: EstimateQuestion, value: number): number {
  if (q.answer === 0) return Math.abs(value);
  return Math.abs(value - q.answer) / Math.abs(q.answer);
}
export function checkEstimate(q: EstimateQuestion, value: number): boolean {
  return estimateError(q, value) <= (q.tolerance ?? 0.1) + 1e-9;
}

/** Reihenfolge: Antwort als Liste der Original-Indizes in der vom Spieler gewählten Reihenfolge */
export function checkOrder(q: OrderQuestion, order: number[]): boolean {
  return order.length === q.items.length && order.every((v, i) => v === i);
}
/** Teilpunkte: Anteil der Elemente an korrekter Position */
export function orderScore(q: OrderQuestion, order: number[]): number {
  return order.filter((v, i) => v === i).length / q.items.length;
}

export function checkNumlock(q: NumlockQuestion, code: string): boolean {
  return code.trim() === q.answer;
}

/** Zuordnung: für jeden linken Eintrag i der Index des gewählten rechten Eintrags (Originalindex) */
export function checkMatch(q: MatchQuestion, m: number[]): boolean {
  return m.length === q.pairs.length && m.every((v, i) => v === i);
}
export function matchScore(q: MatchQuestion, m: number[]): number {
  return m.filter((v, i) => v === i).length / q.pairs.length;
}

/** Lückentext: gewählte Wörter je Lücke */
export function checkCloze(q: ClozeQuestion, words: string[]): boolean {
  return words.length === q.gaps.length && words.every((w, i) => norm(w) === norm(q.gaps[i]));
}
export function clozeScore(q: ClozeQuestion, words: string[]): number {
  return words.filter((w, i) => norm(w) === norm(q.gaps[i] ?? '')).length / q.gaps.length;
}
const norm = (s: string) => s.trim().toLowerCase();

export type Answer = { mc: number } | { estimate: number } | { order: number[] } | { numlock: string } | { match: number[] } | { cloze: string[] };

export function check(q: Question, a: Answer): boolean {
  switch (q.type) {
    case 'mc':
    case 'image':
    case 'audio':
      return 'mc' in a && a.mc === q.answer;
    case 'match':
      return 'match' in a && checkMatch(q, a.match);
    case 'cloze':
      return 'cloze' in a && checkCloze(q, a.cloze);
    case 'estimate':
      return 'estimate' in a && checkEstimate(q, a.estimate);
    case 'order':
      return 'order' in a && checkOrder(q, a.order);
    case 'numlock':
      return 'numlock' in a && checkNumlock(q, a.numlock);
  }
}

/** Text der richtigen Lösung (für Auflösung und Bot) */
export function solutionText(q: Question): string {
  switch (q.type) {
    case 'mc':
    case 'image':
    case 'audio':
      return q.options[q.answer];
    case 'match':
      return q.pairs.map(([l, r]) => `${l} → ${r}`).join(' · ');
    case 'cloze':
      return q.gaps.join(' · ');
    case 'estimate':
      return `${q.answer.toLocaleString('de-DE')}${q.unit ? ' ' + q.unit : ''}`;
    case 'order':
      return q.items.join(' → ');
    case 'numlock':
      return q.answer;
  }
}
